import axios from 'axios';

import { decodeJwtPayload } from '~/lib/jwt';
import type {
  AppConfig,
  SessionRequest,
  SessionResponse,
  ShippingAddress,
  TestStatus,
} from './types';

const KSS_TIMEOUT = 20000;

// Always same-origin. In production nginx/CloudFront routes /v1/* to the
// backend; in development the Vite proxy does, so there is no CORS either way.
// Point the proxy somewhere else with VITE_API_TARGET — see vite.config.ts.
const baseURL = '/';

const SESSION_ENDPOINT = '/v1/kss';
const TEST_ENDPOINT = '/v1/test';
const CONFIG_ENDPOINT = '/v1/config';

export const defaultShippingAddress: ShippingAddress = {
  given_name: 'Klara',
  family_name: 'Joyce',
  street_address: 'Sveavägen 42',
  postal_code: '11134',
  city: 'Stockholm',
  country: 'SE',
  email: 'partner.kss@kustom.com',
  phone: '+46708800000',
};

// Both are also defaulted in the Shipwreck backend when we do not send them.
// Change them in both places.
export const defaultLocale = 'sv-SE';
export const defaultCurrency = 'SEK';

const client = axios.create({ baseURL, timeout: KSS_TIMEOUT });

export function getConfig() {
  return client.get<AppConfig>(CONFIG_ENDPOINT);
}

export function createKssSession(request: SessionRequest) {
  return client.post<SessionResponse>(SESSION_ENDPOINT, {
    locale: request.locale,
    currency: request.currency,
    // Always matched the TMS identifier; there is no separate field for it.
    klarna_mid: request.aggregator.identifier,
    aggregator: request.aggregator,
    recaptcha_answer: request.recaptcha_answer,
    shipping_address: {
      ...defaultShippingAddress,
      ...request.shipping_address,
    },
  });
}

export function postSelectedOption(
  request: Pick<SessionRequest, 'aggregator' | 'recaptcha_answer'> & {
    clientToken: string;
  },
) {
  const { session_id } = decodeJwtPayload<{ session_id: string }>(
    request.clientToken,
  );

  return client.put(SESSION_ENDPOINT, {
    aggregator: request.aggregator,
    action: 'finalize',
    session_id,
    recaptcha_answer: request.recaptcha_answer,
  });
}

export function runTest(
  request: Pick<
    SessionRequest,
    'locale' | 'aggregator' | 'shipping_address' | 'recaptcha_answer'
  >,
) {
  return client.post(TEST_ENDPOINT, request);
}

/**
 * The backend answers POST /v1/test with an absolute Location header naming
 * its own host. Following that verbatim leaves same-origin — which is CORS in
 * development, and breaks in production whenever the public hostname differs
 * from the one the service knows itself by. Only the path is ours to trust.
 */
export function testSessionPath(location: string) {
  const { pathname, search } = new URL(location, window.location.origin);
  return `${pathname}${search}`;
}

export function readTestSession(testSession: string) {
  return client.get<TestStatus>(testSessionPath(testSession));
}
